import React, { useEffect } from 'react';
import { SharedBranding } from '../../../services';
import { DEFAULT_BRANDING, fontHrefFor, shareThemeVars } from './theme_utils';

// #region BrandedShareThemeProps
export interface BrandedShareThemeProps {
    branding: SharedBranding | null;
    // Preview mode: the wrapper only themes its subtree (it must not stretch to
    // fill the dashboard page or force the page background).
    scoped?: boolean;
    className?: string;
    children: React.ReactNode;
}

// Tracks which Google Fonts stylesheets have already been injected so the
// preview re-rendering on every keystroke never duplicates <link> tags.
const injectedFontHrefs = new Set<string>();

// #region ensureFontLoaded
const ensureFontLoaded = (href: string): void => {
    if (typeof document === 'undefined' || injectedFontHrefs.has(href)) return;
    if (document.querySelector(`link[href="${href}"]`)) {
        injectedFontHrefs.add(href);
        return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    // A failed font load is not fatal — the CSS stack falls back to Inter/system.
    document.head.appendChild(link);
    injectedFontHrefs.add(href);
};

// #region BrandedShareTheme
// Sets the --share-* custom properties from the designer's branding so every
// share component themes itself with the same tokens in the live page and in
// the dashboard preview. Light mode only: the customer page never follows
// prefers-color-scheme.
export const BrandedShareTheme: React.FunctionComponent<BrandedShareThemeProps> = (props: BrandedShareThemeProps): JSX.Element => {
    const { branding, scoped = false, className = '', children } = props;
    const resolved: SharedBranding = branding ?? DEFAULT_BRANDING;

    useEffect(() => {
        ensureFontLoaded(fontHrefFor(resolved.font_key));
    }, [resolved.font_key]);

    return (
        <div
            style={shareThemeVars(resolved)}
            className={`font-share text-share-text ${scoped ? '' : 'min-h-dvh bg-share-surface'} ${className}`}
        >
            {children}
        </div>
    );
};
